import { isEmpty, isSessionTimeExpired } from "utility";
import { setSessionExpiryModalVisibility } from "redux/auth/actions";
import { updateAccountOwnerDetails } from "redux/UserDetails/actions";
import { FIELD_CONFIG } from "./config";
import alerts from "constants/alerts";

/**
 * Calculate snackbar top position based on scroll
 */
export const calculateSnackbarTop = (scrollY) => {
  const headerHeight = 80;
  const stickyThreshold = 200;
  const initialOffset = 240;
  const stickyOffset = 0;

  if (scrollY <= 0) {
    return headerHeight + initialOffset;
  } else if (scrollY < stickyThreshold) {
    const progress = scrollY / stickyThreshold;
    return headerHeight + initialOffset - (progress * (initialOffset - stickyOffset));
  } else {
    return headerHeight + stickyOffset;
  }
};

/**
 * Get region name from ID
 */
export const getRegionName = (id, allRegionList) => {
  let preSelectedRegionName = "";
  allRegionList?.filter((ele) => {
    if (parseInt(ele?.region_id) === parseInt(id)) {
      preSelectedRegionName = ele?.region_code;
      return true;
    }
    return false;
  });
  return preSelectedRegionName;
};

/**
 * Get region ID from name
 */
export const getRegionId = (name, allRegionList) => {
  let regionId = null;
  allRegionList?.filter((ele) => {
    if (ele?.region_code === name) {
      regionId = ele?.region_id;
      return true;
    }
    return false;
  });
  return parseInt(regionId);
};

/**
 * Clear field error
 */
export const clearError = (fieldName, setErrorState, VALIDATION_FIELDS) => {
  if (fieldName) {
    setErrorState(prev => ({
      ...prev,
      fieldErrors: {
        ...prev.fieldErrors,
        [fieldName]: false
      },
      fieldMessages: {
        ...prev.fieldMessages,
        [fieldName]: ""
      }
    }));
  } else {
    setErrorState({
      show: false,
      fieldErrors: { ...VALIDATION_FIELDS },
      fieldMessages: { ...VALIDATION_FIELDS }
    });
  }
};

/**
 * Handle field value change with validation
 */
export const handleValueChange = (type, value, params) => {
  const {
    accountHolderState,
    setAccountHolderState,
    validationTriggered,
    errorState,
    setErrorState,
    snackbarState,
    onApiResponse,
    dismissSnackbar
  } = params;

  setAccountHolderState((prev) => {
    const newState = { ...prev, [type]: value };

    if (validationTriggered) {
      const config = FIELD_CONFIG?.[type];
      let fieldError = false;
      let fieldMessage = "";

      if (config?.validation?.required && isEmpty(value)) {
        fieldError = true;
        fieldMessage = config?.message || alerts?.incorrect_details;
      }

      // Special validation for zip_code length
      if (type === 'zip_code' && !isEmpty(value) && config?.validation?.length) {
        const zipValue = typeof value === 'string' || typeof value === 'number' ? `${value}`.trim() : '';
        if (zipValue.length !== config.validation.length) {
          fieldError = true;
          fieldMessage = alerts?.zipcode_length_error;
        }
      }

      setErrorState(prev => ({
        ...prev,
        show: true,
        fieldErrors: { ...prev?.fieldErrors, [type]: fieldError },
        fieldMessages: { ...prev?.fieldMessages, [type]: fieldMessage || "" }
      }));

      const updatedFieldErrors = { ...errorState?.fieldErrors, [type]: fieldError };
      const hasAnyErrors = Object.values(updatedFieldErrors || {}).some(Boolean);

      if (fieldError && !snackbarState.show) {
        onApiResponse(alerts?.incorrect_or_missing_details, "error");
      } else if (!fieldError && !hasAnyErrors && snackbarState.show) {
        dismissSnackbar();
      }
    } else {
      setErrorState(prev => {
        if (prev.fieldErrors?.[type]) {
          return {
            ...prev,
            fieldErrors: { ...prev.fieldErrors, [type]: false },
            fieldMessages: { ...prev.fieldMessages, [type]: "" },
          };
        }
        return prev;
      });

      if (snackbarState.show) {
        dismissSnackbar();
      }
    }

    return newState;
  });
};

/**
 * Validate all fields
 */
export const validateAllFields = (accountHolderState, phoneList, VALIDATION_FIELDS) => {
  const fieldErrors = { ...VALIDATION_FIELDS };
  const fieldMessages = { ...VALIDATION_FIELDS };
  let hasErrors = false;
  let errorMessage = "";

  Object.keys(FIELD_CONFIG || {}).forEach(fieldName => {
    if (fieldName !== 'phoneList') {
      const value = accountHolderState?.[fieldName];
      const config = FIELD_CONFIG?.[fieldName];

      if (config?.validation?.required && isEmpty(value)) {
        fieldErrors[fieldName] = true;
        fieldMessages[fieldName] = config?.message || alerts?.incorrect_or_missing_details;
        errorMessage = alerts?.incorrect_or_missing_details
        hasErrors = true;
      }

      // Special validation for zip_code length
      if (fieldName === 'zip_code' && !isEmpty(value) && config?.validation?.length) {
        const zipValue = typeof value === 'string' || typeof value === 'number' ? `${value}`.trim() : '';
        if (zipValue.length !== config.validation.length) {
          fieldErrors[fieldName] = true;
          fieldMessages[fieldName] = alerts?.zipcode_length_error;
          errorMessage = alerts?.zipcode_length_error;
          hasErrors = true;
        }
      }
    }
  });

  if (phoneList?.length === 0) {
    fieldErrors.phoneList = true;
    fieldMessages.phoneList = FIELD_CONFIG.phoneList?.message || "At least one phone number is required";
    errorMessage = fieldMessages?.phoneList
    hasErrors = true;
  }

  return { fieldErrors, fieldMessages, hasErrors, errorMessage };
};

/**
 * Handle form submission
 */
export const handleSubmit = async (params) => {
  const {
    accountHolderState,
    phoneList,
    phoneTypeList,
    accountHolderData,
    allRegionList,
    setValidationTriggered,
    setErrorState,
    onApiResponse,
    dispatch,
    history,
    handleApiResponse,
    setChangesSaveModal,
    VALIDATION_FIELDS
  } = params;

  let timeDifference = await isSessionTimeExpired();

  setValidationTriggered(true);

  const { fieldErrors, fieldMessages, hasErrors, errorMessage } = validateAllFields(
    accountHolderState,
    phoneList,
    VALIDATION_FIELDS
  );

  if (hasErrors) {
    setErrorState({
      show: true,
      fieldErrors,
      fieldMessages
    });
    onApiResponse(errorMessage ? errorMessage : alerts?.incorrect_or_missing_details, "error");
    return;
  }

  if (timeDifference) {
    setSessionExpiryModalVisibility(dispatch, true);
    return;
  } else {
    let payload = {
      mailing_address: accountHolderState?.street,
      apt_no: accountHolderState?.apt,
      mailing_region_id: getRegionId(accountHolderState?.state, allRegionList),
      mailing_city: accountHolderState?.city,
      mailing_postcode: accountHolderState?.zip_code,
      phone_one: phoneList?.[0]?.phoneNumber ?? false,
      phone_two: phoneList?.[1]?.phoneNumber ?? null,
      phone_type_one: phoneList?.[0]?.type_id ?? null,
      phone_type_two: phoneList?.[1]?.type_id ?? null,
      name: accountHolderData?.firstname + " " + accountHolderData?.lastname,
      reference_phone_type_one: phoneList[0]?.type?.reference_phone_type_id ?? phoneTypeList[0]?.reference_phone_type_id,
      reference_phone_type_two: phoneList[1]?.type?.reference_phone_type_id ?? phoneTypeList[0]?.reference_phone_type_id,
      is_phone_one_auto_notify: phoneList?.[0]?.auto_notify ? false : true,
      is_phone_two_auto_notify: phoneList?.[1]?.auto_notify ? false : true,
    };
    updateAccountOwnerDetails(
      dispatch,
      payload,
      history,
      handleApiResponse,
      setChangesSaveModal
    );
  }
};

/**
 * Handle phone list changes and validation
 */
export const handlePhoneListChange = (newPhoneList, params) => {
  const {
    setPhoneList,
    validationTriggered,
    errorState,
    setErrorState,
    snackbarState,
    onApiResponse,
    dismissSnackbar
  } = params;

  setPhoneList(newPhoneList);

  if (validationTriggered) {
    if (newPhoneList?.length > 0 && errorState.fieldErrors.phoneList) {
      clearError('phoneList', setErrorState, params.VALIDATION_FIELDS);

      const hasOtherErrors = Object.keys(errorState.fieldErrors).some(fieldKey =>
        fieldKey !== 'phoneList' && errorState.fieldErrors[fieldKey]
      );
      if (!hasOtherErrors) {
        dismissSnackbar();
      }
    } else if (newPhoneList?.length === 0 && !errorState.fieldErrors.phoneList) {
      setErrorState(prev => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, phoneList: true },
        fieldMessages: { ...prev.fieldMessages, phoneList: FIELD_CONFIG.phoneList?.message || "At least one phone number is required" }
      }));
      onApiResponse(alerts?.incorrect_or_missing_details, "error");
    }
  }
};

